import React, { useEffect, useState } from 'react';
import { X, User, Mail, Phone, Loader2, ShieldCheck, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PAYSTACK_PUBLIC_KEY, isPaystackConfigured } from '../../config/paystack';

interface RegistrationFormProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPackage?: string | null;
}

const packages: Record<string, { name: string; price: number }> = {
  modular: { name: 'Zero2Export™ Modular', price: 80000 },
  full: { name: 'Zero2Export™', price: 160000 },
  plus: { name: 'Zero2Export™ + BCEM', price: 310000 },
};

const Zero2ExportRegistrationForm: React.FC<RegistrationFormProps> = ({
  isOpen,
  onClose,
  selectedPackage,
}) => {
  const [form, setForm] = useState({ fullName: '', email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (window.PaystackPop) {
      setReady(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.async = true;
    script.onload = () => setReady(true);
    script.onerror = () => setError('Failed to load payment processor. Please refresh.');
    document.body.appendChild(script);
  }, [isOpen]);

  if (!isOpen) return null;

  const pkg = selectedPackage ? packages[selectedPackage] : null;

  const handlePay = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!pkg) {
      setError('No package selected.');
      return;
    }
    if (!isPaystackConfigured()) {
      setError('Payment is not configured. Please contact support.');
      return;
    }
    if (!window.PaystackPop) {
      setError('Payment processor is still loading. Please wait.');
      return;
    }

    setLoading(true);
    try {
      const handler = window.PaystackPop.setup({
        key: PAYSTACK_PUBLIC_KEY,
        email: form.email,
        amount: pkg.price * 100,
        currency: 'NGN',
        metadata: {
          custom_fields: [
            { display_name: 'Full Name', variable_name: 'full_name', value: form.fullName },
            { display_name: 'Phone', variable_name: 'phone', value: form.phone },
            { display_name: 'Package', variable_name: 'package', value: pkg.name },
          ],
        },
        callback: () => {
          setLoading(false);
          onClose();
        },
        onClose: () => setLoading(false),
      });
      handler.openIframe();
    } catch (err) {
      setLoading(false);
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />
        <div className="flex min-h-screen items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 px-6 py-6 text-white relative">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-white/80 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-2 text-blue-100 text-sm mb-1">
                <Zap className="h-4 w-4" /> Quick Checkout
              </div>
              <h3 className="text-xl font-bold">{pkg?.name ?? 'Zero2Export™'}</h3>
              {pkg && (
                <div className="mt-3 flex items-baseline gap-2">
                  <span className="text-3xl font-bold">₦{pkg.price.toLocaleString()}</span>
                </div>
              )}
            </div>

            <form onSubmit={handlePay} className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Enter your details to proceed to secure payment. You'll receive class access
                details by email after payment.
              </p>

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    required
                    value={form.fullName}
                    onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                    className="w-full pl-10 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                    placeholder="Your full name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full pl-10 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    required
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full pl-10 rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                    placeholder="+234 800 000 0000"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !ready}
                className="w-full py-3.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold rounded-lg transition flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><Loader2 className="h-5 w-5 animate-spin" /> Redirecting to Paystack…</>
                ) : (
                  <>Proceed to Payment{pkg ? ` · ₦${pkg.price.toLocaleString()}` : ''}</>
                )}
              </button>

              <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                <ShieldCheck className="h-4 w-4 text-blue-600" />
                Secured by Paystack · SSL encrypted
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  );
};

export default Zero2ExportRegistrationForm;